import React, { ComponentProps, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import BuilderAppNavbar from '../app-navbar';
import { AppProvider, appStorage, useAppContext } from '../../hooks/use-app';
import ErrorBoundary from '../error-boundary';
import { XLg } from 'react-bootstrap-icons';
import useIdentity from '../../hooks/use-identity';
import BuilderLoginView from '../login-view';
import Builder from '../../index';
import { ToastContainer, CloseButton } from 'react-toastify';
import { DialogContainer } from '../dialog';
import { BuilderSpinnerBar } from '../spinner-boundary';

type CloseButtonProps = ComponentProps<typeof CloseButton>;

function AppStateWriter() {
    const { state } = useAppContext();

    useEffect(() => {
        appStorage.setItem('default', state);
    }, [state]);

    return null;
}

function BuilderToastCloseBtn({ closeToast }: CloseButtonProps) {
    return (
        <button onClick={closeToast} className="feb-toast__close-btn">
            <XLg />
        </button>
    );
}

function useAppLocale() {
    const app = useAppContext();
    const [, setForceUpdate] = useState(0);
    useEffect(() => {
        Builder.i18n.setItem('locale', app?.state?.locale);
        setForceUpdate((u) => u + 1);
    }, [app?.state?.locale, setForceUpdate]);
    return app?.state?.locale ?? 'default';
}

function BuilderApp({
    className = '',
    ...props
}: React.ComponentProps<'div'> & { className?: string }) {
    const { loggedIn, syncAndRenewSession } = useIdentity();

    const [didSync, setDidSync] = useState(false);

    useEffect(() => {
        let mounted = true;

        syncAndRenewSession().then(() => {
            mounted && setDidSync(true);
        });

        // sync server side identity every minute or half of the default session max age (whatever is shorter)
        const id = setInterval(
            () => {
                mounted && syncAndRenewSession();
            },
            Math.min(1000 * 60, Builder.sessionMaxAge / 2)
        );

        return () => {
            mounted = false;
            clearInterval(id);
        };
    }, [syncAndRenewSession, setDidSync]);

    useAppLocale();

    const loggedInContent = (
        <>
            <BuilderAppNavbar />
            <Routes>
                {Builder.routes &&
                    Object.entries(Builder.routes).map(([key, route]) => (
                        <route.type key={key} {...route.props} />
                    ))}
                {Builder.defaultView && (
                    <Route path="*" element={Builder.defaultView} />
                )}
            </Routes>
        </>
    );

    const loggedOutContent = (
        <Routes>
            <Route path="*" element={<BuilderLoginView />} />
        </Routes>
    );

    const content = didSync ? (
        loggedIn ? (
            loggedInContent
        ) : (
            loggedOutContent
        )
    ) : (
        <BuilderSpinnerBar />
    );

    return (
        <>
            <AppStateWriter />
            <BrowserRouter basename={Builder.basename}>
                <div {...props} className={`feb feb-app ${className}`}>
                    <ErrorBoundary>{content}</ErrorBoundary>
                    <ToastContainer
                        position="bottom-center"
                        autoClose={2000}
                        progressStyle={{ backgroundColor: 'var(--bs-primary)' }}
                        closeButton={BuilderToastCloseBtn}
                    />
                    <DialogContainer />
                </div>
            </BrowserRouter>
        </>
    );
}

export default function BuilderAppWrap(
    props: React.ComponentProps<typeof BuilderApp>
) {
    return (
        <AppProvider value={appStorage.getItem('default', {})}>
            <BuilderApp {...props} />
        </AppProvider>
    );
}
